import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Building, Floor, Town } from 'src/api/types';
import { PlaceType } from 'src/types';
import { getDateTime } from 'src/utils/getDateTime';
import { toTime } from 'src/utils/toTime';
import { validateDate } from 'src/utils/validations/validateDate';

import { getIsMobileWithTouchscreen } from '../agent/selectors';
import { fetchLastBookings } from '../lastBookings/services';
import { startAppListening } from '../listenerMiddleware';
import { sidesheetActions } from '../sidesheet/slice';

import { initialState } from './initialState';
import { fetchParkingPlaces } from './services';
import { calculateDateBounds, calculateParkingTimeError } from './utils';

const getCurrentDate = (town: Town | undefined): string | undefined => {
  if (town?.timeZone === undefined) return undefined;

  const date = getDateTime({
    timezoneOffset: town.timeZone,
  });
  return date.toISOString();
};

export const parkingSlice = createSlice({
  name: 'parking',
  initialState,
  reducers: {
    setTown: (state, action: PayloadAction<Town | undefined>) => {
      state.town = action.payload;

      const date = getCurrentDate(action.payload);
      state.date = date;
    },
    setBuilding: (state, action: PayloadAction<Building | undefined>) => {
      state.building = action.payload;
    },
    setFloor: (state, action: PayloadAction<Floor | undefined>) => {
      state.floor = action.payload;
    },
    clearLocation: state => {
      state.town = undefined;
      state.building = undefined;
      state.floor = undefined;
    },
    setDate: (state, action: PayloadAction<string | undefined>) => {
      state.date = action.payload;

      const timezoneOffset = state.town?.timeZone;
      if (timezoneOffset !== undefined) {
        const { min, max } = calculateDateBounds(timezoneOffset);
        const dateError = validateDate(action.payload, min, max);
        state.dateError = dateError;

        const [from, to] = state.time.split('—');
        const timeError = calculateParkingTimeError(
          action.payload,
          from,
          to,
          state.town?.timeZone,
        );
        state.timeError = timeError;
      }
    },
    setTime: (state, action: PayloadAction<string>) => {
      state.time = action.payload;

      const [from, to] = action.payload.split('—');
      const timeError = calculateParkingTimeError(
        state.date,
        from,
        to,
        state.town?.timeZone,
      );
      state.timeError = timeError;
    },
    validateDate: state => {
      if (state.town?.timeZone !== undefined) {
        const { min, max } = calculateDateBounds(state.town.timeZone);
        const dateError = validateDate(state.date, min, max);
        state.dateError = dateError;
      }
    },
    validateTime: state => {
      const [from, to] = state.time.split('—');
      const timeError = calculateParkingTimeError(
        state.date,
        from,
        to,
        state.town?.timeZone,
      );
      state.timeError = timeError;
    },
    setDateError: (state, action: PayloadAction<string | undefined>) => {
      state.dateError = action.payload;
    },
    setTimeError: (state, action: PayloadAction<string>) => {
      state.timeError = action.payload;
    },
    setParkingId: (state, action: PayloadAction<string | undefined>) => {
      state.parkingId = action.payload;
    },
    setTimeRange: (
      state,
      action: PayloadAction<{ from: Date | undefined; to: Date | undefined }>,
    ) => {
      state.date = action.payload.from?.toISOString();
      state.time = `${toTime(action.payload.from) ?? ''}—${toTime(action.payload.to) ?? ''}`;
    },
    setParkingBookingSheetIsOpened: (state, action: PayloadAction<boolean>) => {
      state.parkingBookingSheetIsOpened = action.payload;
    },
    openParkingBookingSheet: state => {
      state.parkingBookingSheetIsOpened = true;
    },
    closeParkingBookingSheet: state => {
      state.parkingBookingSheetIsOpened = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchParkingPlaces.pending, (state, action) => {
      state.error = undefined;
      if (action.meta.arg) state.loading = true;
      state.currentRequestId = action.meta.requestId;
    });
    builder.addCase(fetchParkingPlaces.rejected, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.error = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchParkingPlaces.fulfilled, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.loading = false;
      state.bookingObjects = action.payload.bookingObjects;

      if (action.meta.arg) state.backgroundData = action.payload.backgroundData;

      state.currentRequestId = undefined;
    });

    builder.addCase(fetchLastBookings.pending, state => {
      state.locationError = undefined;
      state.locationLoading = true;
    });
    builder.addCase(fetchLastBookings.rejected, (state, action) => {
      state.locationError = action.payload;
      state.locationLoading = false;
    });
    builder.addCase(fetchLastBookings.fulfilled, (state, action) => {
      state.locationLoading = false;

      if (state.town && state.building && state.floor) return;

      const parkingLocation = action.payload[PlaceType.Parking];
      const town = parkingLocation?.town.hasParkings
        ? parkingLocation.town
        : undefined;
      const building =
        town && parkingLocation?.building.hasParkings
          ? parkingLocation.building
          : undefined;
      const floor =
        building && parkingLocation?.floor.isParking
          ? parkingLocation.floor
          : undefined;

      state.town = town;
      state.building = building;
      state.floor = floor;

      const date = getCurrentDate(town);
      state.date = date;
    });
  },
});

export const { actions: parkingActions, reducer: parkingReducer } =
  parkingSlice;

export const parkingPlacesPollingStarted = createAction<
  boolean,
  'parking/started'
>('parking/started');
export const parkingPlacesPollingStopped = createAction('parking/stopped');
export const openParking = createAction<string, 'parking/open'>('parking/open');

startAppListening({
  actionCreator: parkingActions.setDate,
  effect: async (action, listenerApi) => {
    await listenerApi.dispatch(parkingPlacesPollingStopped());
    await listenerApi.dispatch(fetchParkingPlaces(false));
    await listenerApi.dispatch(parkingPlacesPollingStarted(false));
  },
});

startAppListening({
  actionCreator: parkingActions.setTime,
  effect: async (action, listenerApi) => {
    await listenerApi.dispatch(parkingPlacesPollingStopped());
    await listenerApi.dispatch(fetchParkingPlaces(false));
    await listenerApi.dispatch(parkingPlacesPollingStarted(false));
  },
});

startAppListening({
  actionCreator: parkingPlacesPollingStarted,
  effect: async (action, listenerApi) => {
    listenerApi.unsubscribe();

    if (action.payload) await listenerApi.dispatch(fetchParkingPlaces(false));

    const refreshTask = listenerApi.fork(async forkApi => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await forkApi.delay(10000);
        // eslint-disable-next-line no-await-in-loop
        await listenerApi.dispatch(fetchParkingPlaces(false));
      }
    });

    await listenerApi.condition(parkingPlacesPollingStopped.match);
    refreshTask.cancel();

    listenerApi.subscribe();
  },
});

startAppListening({
  actionCreator: openParking,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState();

    const isMobile = getIsMobileWithTouchscreen(state);

    listenerApi.dispatch(parkingActions.setParkingId(action.payload));
    if (isMobile) {
      listenerApi.dispatch(parkingActions.openParkingBookingSheet());
    } else {
      listenerApi.dispatch(sidesheetActions.open('parkingBooking'));
    }
  },
});

startAppListening({
  predicate: (action, current, previous) => {
    return (
      current.sidesheet.current !== 'parkingBooking' &&
      previous.sidesheet.current === 'parkingBooking'
    );
  },
  effect: (action, listenerApi) => {
    listenerApi.dispatch(parkingActions.setParkingId(undefined));
  },
});
